// One implementation, shared with the lambdas that ingest these same units.
// See lambda/shared-utils/tests/unit-cases.json for what pins it to the
// Python port.
import { UnitConverter, normalizeUnit } from '../../lambda/shared-utils/unit-conversion.mjs';

export function convertFxRange(fxRange, testAlias, labUnits) {
    if (!fxRange || !labUnits || !fxRange.units) return fxRange;
    if (normalizeUnit(fxRange.units) === normalizeUnit(labUnits)) {
        // Same magnitude, possibly a different spelling (mIU/L vs µIU/mL). Label
        // the band the way the lab labelled the result it sits behind.
        return fxRange.units === labUnits ? fxRange : { ...fxRange, units: labUnits };
    }

    const name = testAlias || '';
    const convLow = fxRange.fx_low != null
        ? UnitConverter.convert(name, fxRange.fx_low, fxRange.units, labUnits)
        : null;
    const convHigh = fxRange.fx_high != null
        ? UnitConverter.convert(name, fxRange.fx_high, fxRange.units, labUnits)
        : null;

    if (convLow === null && convHigh === null) return null;
    return { ...fxRange, fx_low: convLow, fx_high: convHigh, units: labUnits };
}

/**
 * Coerce a lab result to a plottable number.
 *
 * Lab documents carry values in shapes Number() rejects outright: thousands
 * separators ("2,308"), trailing units ("17.2 g/dL", "108 mL/min/1.73m2"),
 * derivation notes ("71 mg/dL (calc)") and spelled-out comparators
 * ("> OR = 40"). Each of those used to become NaN, which the chart renders as a
 * "Text result" message. Take the leading numeric token instead.
 *
 * Returns NaN only when there is genuinely no number to plot ("NEGATIVE") or
 * when the value is a ratio/titer ("1:160"), whose first token is not a
 * magnitude.
 *
 * SYNC: lambda/shared-utils/normalization.{py,mjs} and
 * lambda/health-recommendations/diagnostic_knots.py implement the same logic.
 * All four are pinned to lambda/shared-utils/tests/result-parsing-cases.json.
 */
function resultNumberText(raw) {
    if (raw == null) return null;
    let str = String(raw).trim();
    if (!str) return null;

    // Comparator prefixes: "<", ">=", "> OR = 40", "LESS THAN 5"
    str = str.replace(/^(?:<|>|≤|≥)\s*(?:or)?\s*=?\s*/i, '');
    str = str.replace(/^(?:less|greater)\s+than(?:\s+or\s+equal(?:\s+to)?)?\s*/i, '');

    // Ratios and titers have no single magnitude — don't take the numerator.
    if (/^\d+\s*:\s*\d+/.test(str)) return null;

    const match = str.match(/^[+-]?(?:\d{1,3}(?:,\d{3})+|\d+)?(?:\.\d+)?(?:[eE][+-]?\d+)?/);
    if (!match || !/\d/.test(match[0])) return null;
    return match[0];
}

export function parseResultValue(raw) {
    if (typeof raw === 'number') return raw;
    const number = resultNumberText(raw);
    if (number === null) return NaN;
    return Number(number.replace(/,/g, ''));
}

export const isBloodTest = (file) => {
    if (!file || !file.key) return false;
    const key = file.key.toLowerCase();
    return key.includes("blood") && !key.endsWith("blood-summary.json");
};

// Extract test date from a file object (header or filename fallback)
// Handles both file.data.header.testdate and file.header.testdate structures
export const getFileTestDate = (file) =>
    file?.data?.header?.testdate || file?.header?.testdate || file?.key?.match(/\d{8}/)?.[0] || null;

export const filterAndSortBloodTests = (files) => {
    return files ? files.filter(isBloodTest).sort((a, b) => a.data.header.testdate - b.data.header.testdate) : []
}

/**
 * Returns the canonical color for a given range status.
 * Matches the zone colors used in DetailedTestChart and LinePlot shading.
 *   abnormal  (high/low)           → #ef4444 (red)
 *   dysfunctional (elevated/diminished) → #eab308 (yellow)
 *   optimal                        → #22c55e (green)
 */
export function getStatusColor(status) {
    switch (status) {
        case 'high':
        case 'low':
            return '#ef4444';
        case 'elevated':
        case 'diminished':
            return '#eab308';
        case 'optimal':
            return '#22c55e';
        default:
            return '#6b7280';
    }
}

/**
 * Classify a result against its lab and functional ranges.
 *
 * Returns "unranged" when there is nothing to judge it by — a text result
 * ("Negative", "None Seen") or no usable range at all. That used to fall through
 * to "optimal", so every unrecognized marker showed a green Optimal pill.
 */
export function getRangeStatus (result, llow, lhigh, fxlow, fxhigh) {
    const numResult = parseResultValue(result);
    if (!Number.isFinite(numResult)) return "unranged";
    const numLlow = llow != null ? Number(llow) : null;
    const numLhigh = lhigh != null ? Number(lhigh) : null;
    let numFxlow = fxlow != null ? Number(fxlow) : null;
    let numFxhigh = fxhigh != null ? Number(fxhigh) : null;

    // Sanity check: if functional range is entirely outside lab range OR
    // differs by >50x (unit mismatch, e.g. cells/µL vs x10³/µL), ignore it
    if (numLlow !== null && numLhigh !== null) {
        if ((numFxhigh !== null && numFxhigh < numLlow) ||
            (numFxlow !== null && numFxlow > numLhigh)) {
            numFxlow = null;
            numFxhigh = null;
        }
    }
    if (numLhigh > 0 && numFxhigh !== null && numFxhigh > 0) {
        const ratio = numLhigh / numFxhigh;
        if (ratio > 50 || ratio < 1 / 50) {
            numFxlow = null;
            numFxhigh = null;
        }
    }

    if (numLlow === null && numLhigh === null && numFxlow === null && numFxhigh === null) {
        return "unranged";
    }

    if (numLhigh !== null && numResult > numLhigh) {
        return "high";
    } else if (numFxhigh !== null && numResult > numFxhigh) {
        return "elevated";
    } else if (numLlow !== null && numResult < numLlow) {
        return "low";
    } else if (numFxlow !== null && numResult < numFxlow) {
        return "diminished";
    } else {
        return "optimal";
    }
}

/** User-facing label for a getRangeStatus() value. */
export function getStatusLabel (status) {
    if (status === "unranged") return "No range";
    return status ? status.charAt(0).toUpperCase() + status.slice(1) : "";
}

export function formatDate (dateString) {
    if (!dateString) return 'N/A';
    const str = dateString.toString();
    if (str.length === 8) {
        const year = str.substring(0, 4);
        const month = str.substring(4, 6) - 1;
        const day = str.substring(6, 8);
        const date = new Date(year, month, day);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    }
    return str;
};

/**
 * Format an ISO 8601 date string (e.g. "2025-03-21T12:00:00Z") to a short locale string.
 * Falls back to 'N/A' for falsy input or 'Never' for null expirations.
 */
export function formatDateISO(isoString) {
    if (!isoString) return 'N/A';
    const date = new Date(isoString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/**
 * Extract up to 2 initials from a name string.
 * @param {string} name - Full name
 * @param {string} fallback - Character(s) to return when name is falsy (default 'U')
 */
export function getInitials(name, fallback = 'U') {
    if (!name) return fallback;
    return name.split(' ').map(w => w[0]).join('').toUpperCase().substring(0, 2);
}

/**
 * Build a display name from a blood test file object: "TestType - Date"
 */
export function getFileDisplayName(file) {
    const header = file.data?.header;
    const testType = header?.type || header?.testname || 'Blood Test';
    const date = header?.testdate ? formatDate(header.testdate.toString()) : 'No Date';
    return `${testType} - ${date}`;
}

export const DURATION_OPTIONS = [
    { label: '1 Day', days: 1 },
    { label: '1 Week', days: 7 },
    { label: '1 Month', days: 30 },
    { label: '90 Days', days: 90 },
    { label: 'Permanent', days: null },
];

export const aggregateLabTests = (tests, normalizeTestName) => {
    const aggregated = tests.reduce((acc, test) => {
        const displayName = normalizeTestName(test.alias || test.test_name);
        const key = displayName.toLowerCase();

        if (!acc[key]) {
            acc[key] = {...test, test_name: displayName, alias: displayName, results: []};
        } else {
            // Update main properties if this test is more recent
            const existingDate = acc[key].testdate;
            const newDate = test.testdate;
            if (newDate && (!existingDate || newDate > existingDate)) {
                acc[key].result = test.result;
                acc[key].lab_low = test.lab_low;
                acc[key].lab_high = test.lab_high;
                acc[key].units = test.units;
                acc[key].testdate = test.testdate;
            }
        }

        acc[key].results.push({
            result: test.result,
            testdate: test.testdate,
            lab_low: test.lab_low,
            lab_high: test.lab_high,
            units: test.units,
        });
        return acc;
    }, {})

    return Object.values(aggregated)
}

/**
 * Count decimal places in a result, for precision preservation when averaging.
 * Measures the numeric token only, so a trailing unit ("5.0 %") is not counted.
 */
export function resultPrecision(raw) {
    const number = resultNumberText(raw);
    if (number === null) return 0;
    const idx = number.indexOf('.');
    return idx >= 0 ? number.length - idx - 1 : 0;
}

export function normalizeAggregatedResults(aggregatedTest) {
    if (!aggregatedTest.results || aggregatedTest.results.length <= 1) return aggregatedTest;

    if (aggregatedTest.lab_low == null || aggregatedTest.lab_high == null) return aggregatedTest;
    const targetLow = Number(aggregatedTest.lab_low);
    const targetHigh = Number(aggregatedTest.lab_high);
    const targetUnits = aggregatedTest.units;

    if (!Number.isFinite(targetLow) || !Number.isFinite(targetHigh)) return aggregatedTest;

    const targetPrecision = Math.max(resultPrecision(String(targetLow)), resultPrecision(String(targetHigh)));
    const targetSpan = targetHigh - targetLow;
    const rangePrecision = targetSpan > 0 ? Math.max(0, Math.ceil(-Math.log10(targetSpan)) + 1) : 0;

    const normalizedResults = aggregatedTest.results.map(r => {
        let convertedResult = parseResultValue(r.result);
        let decimals = resultPrecision(r.result);

        if (r.lab_low == null || r.lab_high == null) return { ...r, result: convertedResult };
        let rLow = Number(r.lab_low);
        let rHigh = Number(r.lab_high);

        if (r.units && targetUnits && normalizeUnit(r.units) !== normalizeUnit(targetUnits)) {
            const conv = UnitConverter.convert(aggregatedTest.test_name, convertedResult, r.units, targetUnits);
            if (conv !== null) {
                const convLow = UnitConverter.convert(aggregatedTest.test_name, rLow, r.units, targetUnits);
                const convHigh = UnitConverter.convert(aggregatedTest.test_name, rHigh, r.units, targetUnits);
                let convPrecision = 0;
                if (convertedResult !== 0) {
                    const ratio = Math.abs(conv / convertedResult);
                    if (ratio < 1) convPrecision = Math.ceil(-Math.log10(ratio));
                }
                convertedResult = conv;
                if (convLow !== null) rLow = convLow;
                if (convHigh !== null) rHigh = convHigh;
                decimals = Math.max(decimals, targetPrecision, convPrecision);
            }
        }

        if (!Number.isFinite(rLow) || !Number.isFinite(rHigh)) return { ...r, result: convertedResult };
        if (rLow === targetLow && rHigh === targetHigh) return { ...r, result: convertedResult };

        const normalized = UnitConverter.normalizeToRange(convertedResult, rLow, rHigh, targetLow, targetHigh);
        if (normalized == null) return { ...r, result: convertedResult };
        decimals = Math.max(decimals, rangePrecision);
        return { ...r, result: Number(normalized.toFixed(decimals)) };
    });

    return { ...aggregatedTest, results: normalizedResults };
}

/**
 * Process an uploaded file response into a standardized file object
 * Ensures body entries have normalized alias fields
 * @param {Object} uploadedFile - Raw upload response with key and data
 * @param {Function} normalizeTestName - Function to normalize test names to aliases
 * @returns {Object} Processed file object with { key, data }
 */
export const processUploadedFile = (uploadedFile, normalizeTestName) => {
    let parsedData = typeof uploadedFile.data === 'string'
        ? JSON.parse(uploadedFile.data)
        : uploadedFile.data;

    // Ensure body entries have alias field
    if (parsedData.body && Array.isArray(parsedData.body)) {
        if (!parsedData.body[0]?.alias) {
            parsedData.body = parsedData.body.map(entry => ({
                ...entry,
                alias: normalizeTestName(entry.test_name)
            }));
        }
    }

    return {
        key: uploadedFile.key,
        // An upload response carries no S3 write time; it arrives on the next
        // retrieve. Left null deliberately - reconcileMerges treats an undateable
        // file as newer than any merge, so a file we cannot date is never deleted.
        lastModified: uploadedFile.lastModified ?? null,
        data: parsedData
    };
};

export const createFileName = (header) => {
    const lastName = (header.last_name || "unknown").replace(/\s+/g, "_");
    const firstName = (header.first_name || "unknown").replace(/\s+/g, "_");
    
    const now = new Date();
    const testDate = header.testdate || now.toISOString().split('T')[0].replace(/-/g, "");
    const testTime = `${now.getHours().toString().padStart(2, '0')}_${now.getMinutes().toString().padStart(2, '0')}`;

    const fileName = `${lastName}_${firstName}_blood_${testDate}_${testTime}.json`;

    return fileName;
};


/**
 * Normalize a lab test date to ISO `YYYY-MM-DD`.
 *
 * Lab files store `header.testdate` as a compact `YYYYMMDD` value that may be a
 * number (`20251029`) or a string (`"20251029"`); some interim/demo paths write
 * an ISO date or full ISO timestamp instead. The recommendations lambda parses
 * the date with `strptime(..., '%Y-%m-%d')`, so everything must reach it in ISO
 * form.
 *
 * @param {number|string|null|undefined} testdate - Raw testdate in any of the above shapes
 * @returns {string|null} `YYYY-MM-DD`, or null when the value is missing/unparseable
 */
export function toIsoDate(testdate) {
    if (testdate === null || testdate === undefined || testdate === '') return null;

    const str = String(testdate).trim();

    // Compact YYYYMMDD
    if (/^\d{8}$/.test(str)) {
        return `${str.slice(0, 4)}-${str.slice(4, 6)}-${str.slice(6, 8)}`;
    }

    // Already ISO date, or an ISO timestamp we can truncate
    const isoMatch = str.match(/^(\d{4}-\d{2}-\d{2})(?:[T ].*)?$/);
    if (isoMatch) return isoMatch[1];

    return null;
}

/**
 * The most recent test date across a set of lab tests, as ISO `YYYY-MM-DD`.
 *
 * Aggregated tests carry a per-marker `testdate` (most-recent-wins merging in
 * aggregateLabTests), so the newest of them is the effective date of the panel
 * being scored. Normalizing before comparing means mixed compact/ISO inputs
 * sort correctly, since ISO dates order lexicographically.
 *
 * @param {Array<{testdate?: number|string}>} tests - Lab tests carrying `testdate`
 * @returns {string|null} Newest date as `YYYY-MM-DD`, or null when none is known
 */
export function latestTestDate(tests) {
    if (!Array.isArray(tests)) return null;
    const dates = tests
        .map(t => toIsoDate(t?.testdate))
        .filter(Boolean)
        .sort();
    return dates.length ? dates[dates.length - 1] : null;
}
